import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { CrewJobChecklist, ProjectCrewNote } from '../types/crewSlack';

export const PROJECT_CREW_NOTES = 'projectCrewNotes';
export const CREW_JOB_CHECKLISTS = 'crewJobChecklists';

function asDate(value: unknown): Date {
  if (value instanceof Date) return value;
  if (value && typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date(0);
}

export function subscribeProjectCrewNotes(
  projectId: string,
  onRows: (rows: ProjectCrewNote[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, PROJECT_CREW_NOTES),
    where('projectId', '==', projectId),
    orderBy('createdAt', 'desc'),
  );
  return onSnapshot(
    q,
    snap => {
      onRows(
        snap.docs.map(d => {
          const data = d.data();
          return {
            id: d.id,
            projectId: String(data.projectId || projectId),
            text: String(data.text || ''),
            authorUid: String(data.authorUid || ''),
            authorName: String(data.authorName || ''),
            source: data.source ? String(data.source) : undefined,
            kind: data.kind === 'damaged_tool' || data.kind === 'wish_list' || data.kind === 'note'
              ? data.kind
              : 'note',
            createdAt: asDate(data.createdAt),
          };
        }),
      );
    },
    err => onError?.(err),
  );
}

export function subscribeActiveCrewChecklist(
  projectId: string,
  onRow: (row: CrewJobChecklist | null) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, CREW_JOB_CHECKLISTS),
    where('projectId', '==', projectId),
    where('status', '==', 'active'),
  );
  return onSnapshot(
    q,
    snap => {
      if (snap.empty) {
        onRow(null);
        return;
      }
      const d = snap.docs[0];
      const data = d.data();
      onRow({
        id: d.id,
        projectId: String(data.projectId || projectId),
        projectName: data.projectName ? String(data.projectName) : null,
        templateId: data.templateId ? String(data.templateId) : undefined,
        status: String(data.status || 'active'),
        items: Array.isArray(data.items)
          ? data.items.map((item: Record<string, unknown>) => ({
              id: String(item.id || ''),
              title: String(item.title || ''),
              category: item.category ? String(item.category) : undefined,
              required: item.required === true,
              done: item.done === true,
              doneAt: item.doneAt ? asDate(item.doneAt) : null,
              doneByUid: item.doneByUid ? String(item.doneByUid) : null,
              doneByName: item.doneByName ? String(item.doneByName) : null,
              recommendation: item.recommendation ? String(item.recommendation) : null,
            }))
          : [],
        recommendations: Array.isArray(data.recommendations)
          ? data.recommendations.map(String)
          : [],
        createdAt: asDate(data.createdAt),
        updatedAt: asDate(data.updatedAt),
      });
    },
    err => onError?.(err),
  );
}

/** Staff edit of checklist item titles / required flags on an existing instance. */
export async function updateCrewChecklistItems(
  checklistId: string,
  items: CrewJobChecklist['items'],
): Promise<void> {
  await updateDoc(doc(db, CREW_JOB_CHECKLISTS, checklistId), {
    items: items.map(i => ({
      id: i.id,
      title: i.title,
      category: i.category || null,
      required: i.required === true,
      done: i.done === true,
      doneAt: i.doneAt || null,
      doneByUid: i.doneByUid || null,
      doneByName: i.doneByName || null,
      recommendation: i.recommendation || null,
    })),
    updatedAt: serverTimestamp(),
  });
}
